import { ListItem, ListItemText, useTheme } from "@mui/material";
import React, { useState } from "react";

import { useAuth } from "@/providers";
import { EditFieldButton, RobotoLinkHoverUnderline } from "@/shared/components";
import {
  InvocationUploadDestination,
  UploadDestinationType,
} from "@/shared/domain/actions";
import { UpdateScheduledTriggerRequest } from "@/shared/domain/actions/TriggerRecord";

import { DatasetSelector } from "./DatasetSelector";

interface EditableOutputDatasetProps {
  value: InvocationUploadDestination | null;
  onChange: (updates: UpdateScheduledTriggerRequest) => void;
  setErrorText: (errorText: string) => void;
}

function getOutputDatasetId(
  dest: InvocationUploadDestination | null,
): string | null {
  if (dest === null) {
    return null;
  }

  if (dest.destination_type === UploadDestinationType.Dataset) {
    return dest.destination_id;
  }

  return null;
}

export const EditableOutputDataset: React.FC<EditableOutputDatasetProps> = ({
  value,
  onChange,
  setErrorText,
}) => {
  const theme = useTheme();
  const { currentOrganization } = useAuth();

  const [showEditableIcon, setShowEditableIcon] = useState(false);
  const [editable, setEditable] = useState(false);
  const [datasetId, setDatasetId] = useState<string | null>(
    getOutputDatasetId(value),
  );

  const updateTrigger = (newDatasetId: string | null): void => {
    setErrorText("");

    let outputDest: InvocationUploadDestination | null = null;
    if (newDatasetId !== null) {
      outputDest = {
        destination_id: newDatasetId,
        destination_type: UploadDestinationType.Dataset,
      };
    }

    onChange({
      invocation_upload_destination: outputDest,
    });
  };

  return (
    <ListItem
      onMouseOver={() => {
        setShowEditableIcon(true);
      }}
      onMouseLeave={() => {
        setShowEditableIcon(false);
      }}
    >
      <ListItemText
        sx={{ mt: theme.spacing(0.5) }}
        primaryTypographyProps={{ component: "span" }}
        primary={
          <>
            <span>Output Dataset</span>

            <EditFieldButton
              editable={editable}
              showEditableIcon={showEditableIcon}
              onClick={() => {
                if (editable) {
                  updateTrigger(datasetId);
                }

                setEditable(!editable);
              }}
            />
          </>
        }
        secondaryTypographyProps={{ component: "span" }}
        secondary={
          <div>
            {editable && (
              <DatasetSelector
                datasetId={datasetId}
                setDatasetId={setDatasetId}
                currentOrg={currentOrganization}
              />
            )}

            {!editable &&
              (datasetId === null ? (
                "None"
              ) : (
                <RobotoLinkHoverUnderline to={`/datasets/${datasetId}`}>
                  {datasetId}
                </RobotoLinkHoverUnderline>
              ))}
          </div>
        }
      />
    </ListItem>
  );
};
